/**
 * Realtime API — the cheap request/response endpoints the LOS hosts for Azure
 * Web PubSub (the actual WebSocket lives on Web PubSub, not the LOS). Backed by
 * `app/routers/realtime.py`.
 *
 * NOTE: until Web PubSub is provisioned on the backend (glv.webpubsub_endpoint),
 * `negotiate` and `presence/*` return HTTP 503 `realtime_not_configured`. The
 * RealtimeProvider detects that and falls back to inbox polling — the bell still
 * works; only the instant live push is dark until infra is up.
 *
 * See frontend_documentation/realtime-web-pubsub.md.
 */
import { dsHttp } from "../lib/digitalSizerHttp";

// 503 detail string the LOS returns while Web PubSub is not yet configured.
export const REALTIME_NOT_CONFIGURED = "realtime_not_configured";

export const isRealtimeNotConfigured = (err) =>
  err?.response?.status === 503 &&
  err?.response?.data?.detail === REALTIME_NOT_CONFIGURED;

// Realtime is a DEGRADABLE feature: if any of these fail auth, the app must fall
// back to polling — NOT clear the session and bounce to /login. `skipAuthRedirect`
// tells dsHttp to skip the global 401 logout for these background calls (a stale
// realtime socket re-negotiating with an expired token must not log the user out).
const NO_AUTH_BOUNCE = { skipAuthRedirect: true };

export const realtimeApi = {
  // GET /realtime/negotiate → { url, userId, hub }  (url = wss://…?access_token=…)
  negotiate: () =>
    dsHttp.get("/realtime/negotiate", NO_AUTH_BOUNCE).then((r) => r.data),

  // POST /realtime/presence/enter { dealId, connectionId } → join deal:{id} viewer group
  presenceEnter: (dealId, connectionId) =>
    dsHttp
      .post("/realtime/presence/enter", { dealId, connectionId }, NO_AUTH_BOUNCE)
      .then((r) => r.data),

  // POST /realtime/presence/exit { dealId, connectionId } → leave the group
  presenceExit: (dealId, connectionId) =>
    dsHttp
      .post("/realtime/presence/exit", { dealId, connectionId }, NO_AUTH_BOUNCE)
      .then((r) => r.data),

  // POST /realtime/activity → refresh `last_seen` (live-vs-email routing signal)
  activity: () =>
    dsHttp.post("/realtime/activity", {}, NO_AUTH_BOUNCE).then((r) => r.data),
};

export default realtimeApi;
